import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';
import { atomicWriteFile } from './fs-atomic.js';
import { KK_NAVIGATION_DIRECTIVE } from './session-start.js';

/**
 * Sentinel markers around the kenkeep-managed pointer block in the repo's
 * AGENTS.md. The block is the knowledge base's "lobby" for every surface
 * that reads agents files natively (Codex, OpenCode, Cursor, humans on the
 * web UI): a one-paragraph pointer at the entry catalog plus the shared
 * descent directive. Deterministic — no LLM writes here; the catalog itself
 * lives in ENTRY.md and is regenerated by `index rebuild`.
 */
export const AGENTS_BLOCK_START = '<!-- >>> kenkeep:kk-index >>> -->';
export const AGENTS_BLOCK_END = '<!-- <<< kenkeep:kk-index <<< -->';

// The static kk-index pointer block. It enters the knowledge base at the entry
// catalog and then reuses the exact descent wording shipped by the
// SessionStart hook, so the always-on file surface and the hook surface share
// one source of truth and cannot drift. The descent body itself is never
// re-typed here; it comes from KK_NAVIGATION_DIRECTIVE.
export const AGENTS_POINTER = [
  'You are required to load [.ai/kenkeep/ENTRY.md](.ai/kenkeep/ENTRY.md), the small curated entry catalog for this repo. Enter there and descend using progressive disclosure principles.',
  '',
  KK_NAVIGATION_DIRECTIVE,
].join('\n');

function ensureTrailingNewline(s: string): string {
  return s.endsWith('\n') ? s : `${s}\n`;
}

/**
 * Idempotently writes the kenkeep pointer block into `file` (the repo's
 * AGENTS.md): creates the file when absent, replaces an existing block in
 * place, appends when no block exists, and preserves all user content
 * outside the markers. Returns true when the file's bytes changed. Called
 * by `init`, `init --upgrade`, and `index rebuild` so the block tracks the
 * current directive wording without any manual step.
 */
export function ensureAgentsKkBlock(file: string): boolean {
  const block = `${AGENTS_BLOCK_START}\n${AGENTS_POINTER}\n${AGENTS_BLOCK_END}`;
  const existing = existsSync(file) ? readFileSync(file, 'utf8') : '';

  let next: string;
  if (existing.includes(AGENTS_BLOCK_START)) {
    const before = existing.slice(0, existing.indexOf(AGENTS_BLOCK_START));
    const afterStart = existing.indexOf(AGENTS_BLOCK_END);
    const afterRaw = afterStart >= 0 ? existing.slice(afterStart + AGENTS_BLOCK_END.length) : '';
    const after = afterRaw.startsWith('\n') ? afterRaw.slice(1) : afterRaw;
    next = ensureTrailingNewline(`${before}${block}\n${after}`);
  } else if (existing.length === 0) {
    next = `${block}\n`;
  } else {
    const sep = existing.endsWith('\n') ? '' : '\n';
    next = `${existing}${sep}\n${block}\n`;
  }

  if (next === existing) return false;
  atomicWriteFile(file, next);
  return true;
}

export interface AgentsBlockIssue {
  file: string;
  message: string;
  action: string;
}

/**
 * Deterministic drift check for the AGENTS.md pointer block, consumed by
 * `lint` as warn-level findings (never errors — a missing lobby degrades
 * discoverability, it does not corrupt the knowledge base):
 *   - AGENTS.md absent or missing the sentinel block;
 *   - the block's pointer target (.ai/kenkeep/ENTRY.md) absent while the
 *     tree has leaves (an empty, just-initialized KB legitimately has no
 *     catalog yet).
 */
export function checkAgentsKkBlock(
  root: string,
  kkDir: string,
  nodeCount: number
): AgentsBlockIssue[] {
  const issues: AgentsBlockIssue[] = [];
  const agentsFile = join(root, 'AGENTS.md');
  if (!existsSync(agentsFile)) {
    issues.push({
      file: 'AGENTS.md',
      message:
        'AGENTS.md does not exist, so agents-file surfaces never discover the knowledge base',
      action:
        'run `npx kenkeep init --harnesses <id[,id,...]> --upgrade` to seed the pointer block',
    });
    return issues;
  }
  const body = readFileSync(agentsFile, 'utf8');
  if (!body.includes(AGENTS_BLOCK_START) || !body.includes(AGENTS_BLOCK_END)) {
    issues.push({
      file: 'AGENTS.md',
      message: 'AGENTS.md is missing the kenkeep pointer block',
      action: 'run `npx kenkeep index rebuild` (or `init --upgrade`) to restore it',
    });
  }
  if (nodeCount > 0 && !existsSync(join(kkDir, 'ENTRY.md'))) {
    issues.push({
      file: '.ai/kenkeep/ENTRY.md',
      message:
        'the pointer block targets .ai/kenkeep/ENTRY.md, which does not exist despite a populated tree',
      action: 'run `npx kenkeep index rebuild` to regenerate the entry catalog',
    });
  }
  return issues;
}
